import { useId, useMemo, type ReactNode } from 'react';
import type { Membership } from "@lgc/database/types/domains/membership";
import type { MembershipCardData } from './MembershipCardGradient';
import { Card, CardContent } from "#primitives/data-display";
import { GradientOverlay } from "#primitives/data-display";
import {
  gradientVariants,
  type GradientVariant,
} from "#primitives/data-display";
import { ArrowIcon } from "#icons";
import { cn } from '#utils';
import { getSafeSubscriptionColor } from "./constants";
import { formatSubscriptionPrice, ZERO_PRICE_LABEL, INSTANCE_ZERO_PRICE_LABEL } from "./formatSubscriptionPrice";

interface MembershipCardProps {
  membership: Membership | MembershipCardData;
  onClick?: (membership: Membership) => void;
  /** Optional badge text displayed in the gradient header (e.g. "Active") */
  badge?: string;
  /** Compact mode reduces the gradient header height. Defaults to false */
  compact?: boolean;
  className?: string;
  action?: ReactNode;
  /** Override the aria-label on the card's interactive overlay button. Defaults to "Edit {name} membership" */
  overlayLabel?: string;
  /** Whether the overlay button opens a dialog. Set to false for non-dialog interactions (e.g. plan selection). Defaults to true */
  opensDialog?: boolean;
  /** Display context: 'my-plan' styles the member's plan; 'reporting' is a read-only full-gradient summary. Defaults to 'default'. */
  variant?: 'default' | 'my-plan' | 'reporting';
  /** Currency-aware display supplied by reporting; catalogue formatting is unchanged. */
  formattedPrice?: string;
  /** Show a circular theme-colored arrow button at the bottom-right of the card (rendered when no `action` is provided). Defaults to false */
  showArrow?: boolean;
  /**
   * Optional short text in the gradient header (e.g. "3 Hours").
   * With a `badge`: top-right of the badge row. Without one: right of the title, on the title row.
   */
  headerLabel?: ReactNode;
  /**
   * Optional content at the start of the banner row (e.g. an avatar stack), with `headerLabel` at the end.
   * Only used when there is no `badge`.
   */
  bannerStart?: ReactNode;
  /** Where the plan name renders. 'body' moves it from the gradient banner to the top of the card body. Defaults to 'banner' */
  titlePlacement?: 'banner' | 'body';
  /** 'mini' is the compact catalogue card (lesson packages): shorter banner without dots, 20px radius, 2px border, title in the body. Defaults to 'default' */
  layout?: 'default' | 'mini';
  /** 'lg' is the web catalogue card (frame 14546:716904): larger type, 2px border, 20px body radius. Default layout only; ignored by `mini`. Defaults to 'md' */
  size?: 'md' | 'lg';
  /** Corner radius of the default-layout card: '3xl' is 24px (lesson packages) instead of the default 28px. Defaults to 'default' */
  radius?: 'default' | '3xl';
  /** Zero price reads "$0.00" (the member's own instance) instead of "Free". Defaults to true for `variant="my-plan"`, false otherwise */
  instanceZeroPrice?: boolean;
  /** How far the body panel overlaps the banner: 'sm' is 26px (lesson packages) instead of the default 40px. Defaults to 'default' */
  bodyOverlap?: 'default' | 'sm';
}

export const MembershipCard = ({
  membership,
  onClick,
  badge,
  compact = false,
  className,
  action,
  overlayLabel,
  opensDialog = true,
  variant = 'default',
  formattedPrice,
  showArrow = false,
  headerLabel,
  bannerStart,
  titlePlacement = 'banner',
  layout = 'default',
  size = 'md',
  radius = 'default',
  instanceZeroPrice,
  bodyOverlap = 'default',
}: MembershipCardProps) => {
  const safeColor = getSafeSubscriptionColor(membership.color);
  const gradient = gradientVariants[safeColor as GradientVariant];

  const priceParts = useMemo(() => {
    // variant "my-plan" = the member's own membership (instance) → "$0.00";
    // catalog listings → "Free".
    const zeroLabel = (instanceZeroPrice ?? variant === 'my-plan') ? INSTANCE_ZERO_PRICE_LABEL : ZERO_PRICE_LABEL;
    const { text: amount, isPaid } = formatSubscriptionPrice(membership.price, { zeroLabel });

    // A zero-cost or unknown price takes no billing-frequency suffix — nothing recurs.
    if (!isPaid) {
      return {
        amount,
        suffix: null,
        a11yLabel: null,
      };
    }

    switch (membership.billingFrequency) {
      case "monthly":
        return { amount, suffix: "/mo", a11yLabel: "per month" };
      case "quarterly":
        return { amount, suffix: "/qtr", a11yLabel: "per quarter" };
      case "semi-annually":
        return { amount, suffix: "/6mo", a11yLabel: "per six months" };
      case "annually":
        return { amount, suffix: "/yr", a11yLabel: "per year" };
      case "one-time":
      default:
        return { amount, suffix: null, a11yLabel: null };
    }
  }, [membership.price, membership.billingFrequency, variant, instanceZeroPrice]);

  const labelledById = useId();

  if (variant === 'reporting') {
    return (
      <Card as="article" rounded="none" shadow={false} aria-labelledby={labelledById}
        className={cn('relative min-h-56 overflow-hidden rounded-[28px] border border-butter-gray-200 p-[27px] text-white', className)}>
        <GradientOverlay gradient={gradient} to="bottom-right" showDots={false} />
        <div className="relative z-10 flex flex-col gap-7">
          <div className="flex min-h-7 items-center justify-between gap-3">
            {bannerStart}
            {headerLabel && <span className="ml-auto text-base font-medium leading-5" data-testid={`subscription-membership-plan-card-header-label-${membership.id}`}>{headerLabel}</span>}
          </div>
          <div className="flex flex-col gap-6">
            <div>
              <h3 id={labelledById} className="text-[22px] font-semibold leading-[26px]" data-testid={`subscription-membership-plan-card-title-${membership.id}`}>{membership.name}</h3>
              {membership.description && <p className="mt-3 text-base font-medium leading-5">{membership.description}</p>}
            </div>
            <p className="text-[28px] font-semibold leading-8" data-testid={`subscription-membership-plan-card-price-${membership.id}`}>{formattedPrice ?? priceParts.amount}</p>
          </div>
        </div>
      </Card>
    );
  }

  const mini = layout === 'mini';
  const lg = size === 'lg' && !mini;
  const titleInBody = mini || titlePlacement === 'body';
  const showBannerRow = mini || Boolean(badge || bannerStart || (titleInBody && headerLabel));

  const title = (
    <h3 className={lg ? `max-w-[24ch] text-22 font-semibold leading-tight ${titleInBody ? 'text-butter-gray-900' : 'text-white'}` : mini ? "max-w-[24ch] text-lg font-semibold leading-1.1 text-butter-gray-900" : titleInBody ? "max-w-[24ch] text-lg font-semibold leading-tight text-butter-gray-900" : "max-w-[24ch] text-lg font-semibold leading-tight text-white"} id={labelledById}
    data-testid={`subscription-membership-plan-card-title-${membership.id}`}
    >
      {membership.name}
    </h3>
  );

  return (
    <Card
      as="article"
      rounded={mini ? 'none' : '3xl'}
      aria-labelledby={labelledById}
      className={cn(
        "relative flex h-full flex-col overflow-hidden bg-white transition-transform hover:scale-[1.02]",
        mini ? 'rounded-20 border-2 border-butter-gray-100' : lg ? 'rounded-3xl border-2 border-butter-gray-100' : cn(radius === '3xl' ? 'rounded-3xl' : 'rounded-28', variant === 'my-plan' ? 'border-1.5 border-butter-gray-100' : 'ring-1 ring-butter-gray-200'),
        "focus-within:ring-2 focus-within:ring-brand-600 focus-within:ring-offset-2",
        className
      )}
    >
      {/* Interactive overlay covering the whole card — only rendered when `onClick`
          is wired, so display-only cards have no dead, mislabelled tab-stop.
          The `as Membership` cast is intentional and NOT type-safe: arg-reading
          consumers (admin / web-portal edit flows) always pass a full `Membership`,
          while navigation consumers (home plans carousel) pass lightweight
          `MembershipCardData` and ignore the argument. A future caller that does
          both would not be caught at compile time — resolved by the planned
          asChild/Link refactor that renders navigation as a real anchor. */}
      {onClick && (
        <button
          type="button"
          onClick={() => onClick(membership as Membership)}
          aria-haspopup={opensDialog ? 'dialog' : undefined}
          aria-label={overlayLabel ?? `Edit ${membership.name} membership`}
          data-testid={`subscription-membership-plan-card-select-button-${membership.id}`}
          className="absolute inset-0 z-20 cursor-pointer focus:outline-none"
        >
          <span className="sr-only">{overlayLabel ?? `Edit ${membership.name}`}</span>
        </button>
      )}

      <div className={mini ? "relative" : compact ? "relative h-28" : "relative h-36"}>
        <GradientOverlay gradient={gradient} to="bottom-right" showDots={!mini} dotsClassName="translate-x-4" />
        <div className={mini ? "relative z-10 flex w-full flex-col items-start" : `relative z-10 flex h-full w-full flex-col items-start ${variant === 'my-plan' ? 'gap-3' : 'gap-2'} p-6`}>
          {showBannerRow && (
            <div className={mini ? "flex w-full items-center justify-between gap-3 px-6 pt-6 pb-11" : "flex w-full items-center justify-between gap-3"}>
              {badge ? (
                <span className={
                  variant === 'my-plan'
                    ? 'inline-flex h-5.5 w-14 items-center justify-center rounded-full bg-white text-xs leading-none font-medium text-butter-gray-600 shadow-sm'
                    : 'rounded-full bg-white px-3 py-1 text-xs font-medium text-butter-gray-800 shadow-sm'
                }>
                  {badge}
                </span>
              ) : (
                bannerStart
              )}
              {headerLabel && (
                <span
                  className={`ml-auto font-medium leading-none text-white ${lg ? 'text-lg' : 'text-sm'}${mini ? ' flex min-h-6.5 items-center' : ''}`}
                  data-testid={`subscription-membership-plan-card-header-label-${membership.id}`}
                >
                  {headerLabel}
                </span>
              )}
            </div>
          )}
          {titleInBody ? null : headerLabel && !showBannerRow ? (
            <div className="flex w-full items-center justify-between gap-3">
              {title}
              <span
                className={`shrink-0 font-medium leading-none text-white ${lg ? 'text-lg' : 'text-sm'}`}
                data-testid={`subscription-membership-plan-card-header-label-${membership.id}`}
              >
                {headerLabel}
              </span>
            </div>
          ) : (
            title
          )}
        </div>
      </div>
      <CardContent
        spacing="none"
        className={cn(
          "relative z-10 flex flex-1 flex-col bg-white p-6",
          mini ? "-mt-5 gap-1 rounded-20" : cn(bodyOverlap === 'sm' ? '-mt-6.5' : lg ? '-mt-8' : '-mt-10', lg ? 'rounded-20' : 'rounded-3xl', variant === 'my-plan' ? 'gap-4' : lg ? 'gap-6 ring-1 ring-black/5' : 'gap-2 ring-1 ring-black/5')
        )}
      >
        {titleInBody && title}
        <p className={mini ? "text-xs font-medium leading-none text-butter-gray-600 line-clamp-1" : lg ? "text-base font-medium leading-5 text-fg-quaternary line-clamp-2" : cn("text-xs font-medium text-butter-gray-600 line-clamp-2", variant === 'my-plan' && 'leading-tight')}>
          {membership.description}
        </p>

        <div className={cn("flex items-center justify-between", mini ? 'mt-auto pt-4' : variant === 'my-plan' ? '' : 'mt-auto')}>
          <p className="flex items-baseline">
            <span className={mini ? "text-xl font-semibold leading-6 text-butter-gray-900" : lg ? "text-28 font-semibold text-butter-gray-900" : cn("text-lg font-semibold text-butter-gray-900", variant === 'my-plan' && 'leading-tight')} data-testid={`subscription-membership-plan-card-price-${membership.id}`}>
              {formattedPrice ?? priceParts.amount}
            </span>
            {priceParts.suffix ? (
              <>
                <span
                  className="ml-0.5 text-sm font-medium text-butter-gray-500"
                  aria-hidden="true"
                >
                  {priceParts.suffix}
                </span>
                <span className="sr-only">
                  {priceParts.a11yLabel}
                </span>
              </>
            ) : null}
          </p>
          {action ? (
            <div className="relative z-30">{action}</div>
          ) : showArrow ? (
            <span
              aria-hidden="true"
              className="relative z-30 flex h-8 w-8 items-center justify-center rounded-full bg-theme-200"
            >
              <ArrowIcon className="h-3.5 w-3.5 -rotate-45 text-theme-700" />
            </span>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
};
